import { apiClient } from "./apiClient";

const BASE = "/recipes";

export async function fetchRecipesApi({ token, params }) {
    const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
    return apiClient(`${BASE}${queryString}`, {
        token,
        method: "GET",
    });
}

export async function fetchRecipeByIdApi({ token, id }) {
    return apiClient(`${BASE}/${id}`, {
        token,
        method: "GET",
    });
}

export async function createRecipeApi({ token, recipe }) {
    return apiClient(BASE, {
        token,
        method: "POST",
        body: recipe,
    });
}

export async function updateRecipeApi({ token, id, recipe }) {
    return apiClient(`${BASE}/${id}`, {
        token,
        method: "PUT",
        body: recipe,
    });
}

export async function deleteRecipeApi({ token, id }) {
    return apiClient(`${BASE}/${id}`, {
        token,
        method: "DELETE",
    });
}

export async function optimizeRecipeApi({ token, id, prompt }) {
    return apiClient(`${BASE}/${id}/optimize-ai`, {
        token,
        method: "POST",
        body: { prompt }
    });
}

export async function optimizeRecipeContentApi({ token, recipe, prompt }) {
    return apiClient(`${BASE}/optimize-ai`, {
        token,
        method: "POST",
        body: { recipe, prompt }
    });
}

export async function publishRecipeApi({ token, id }) {
    return apiClient(`${BASE}/${id}/publish`, {
        token,
        method: "POST",
    });
}

export async function unpublishRecipeApi({ token, id }) {
    return apiClient(`${BASE}/${id}/unpublish`, {
        token,
        method: "POST",
    });
}

export async function forkRecipeApi({ token, id }) {
    return apiClient(`${BASE}/${id}/fork`, {
        token,
        method: "POST",
    });
}

export async function logFeedbackApi({ token, id, feedback }) {
    return apiClient(`${BASE}/${id}/feedback`, {
        token,
        method: "POST",
        body: feedback
    });
}

export async function toggleFavoriteApi({ token, id }) {
    return apiClient(`${BASE}/${id}/favorite`, {
        token,
        method: "POST",
    });
}

export async function fetchFeedbackApi({ token, id }) {
    return apiClient(`${BASE}/${id}/feedback`, {
        token,
        method: "GET",
    });
}

// Shared polling helper for image generation jobs
async function pollForGeneratedImage(token, jobId) {
    const MAX_ATTEMPTS = 60; // 60 * 2s = 120s timeout
    const POLL_INTERVAL_MS = 2000;

    for (let i = 0; i < MAX_ATTEMPTS; i++) {
        await new Promise(r => setTimeout(r, POLL_INTERVAL_MS));

        try {
            const pollResponse = await apiClient(`/agent/generate-image/${jobId}`, {
                token,
                method: "GET",
                returnBlob: true
            });

            if (pollResponse.type && pollResponse.type.includes("application/json")) {
                const text = await pollResponse.text();
                const data = JSON.parse(text);

                if (data.status === 'pending' || data.status === 'processing') {
                    continue;
                } else if (data.status === 'failed') {
                    throw new Error(data.error || "Generation failed in backend");
                }
            } else {
                return pollResponse;
            }

        } catch (error) {
            console.warn("Polling attempt failed with error:", error);
            if (error.message && error.message.includes("job_failed")) {
                throw error;
            }
            if (error.message && error.message.includes("HTTP 500")) {
                console.error("Server 500 error during polling - fatal?");
            }
        }
    }

    throw new Error("Timeout waiting for image generation");
}

export async function generateRecipeFromUrlApi({ token, url, instructions }) {
    return apiClient(`${BASE}/generate-from-url`, {
        token,
        method: "POST",
        body: { url, instructions }
    });
}

export async function generateHeroShotApi({ token, title, description, ingredients, steps }) {
    const startResponse = await apiClient(`/agent/generate-image`, {
        token,
        method: "POST",
        body: { title, description, ingredients, steps }
    });

    return pollForGeneratedImage(token, startResponse.jobId);
}

export async function generateStepImageApi({ token, title, description, stepNumber, stepInstruction, stepIngredients, cumulativeIngredients, imageSize }) {
    const startResponse = await apiClient(`/agent/generate-image`, {
        token,
        method: "POST",
        body: { title, description, stepNumber, stepInstruction, stepIngredients, cumulativeIngredients, imageSize }
    });

    return pollForGeneratedImage(token, startResponse.jobId);
}
